import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAppDispatch, useAppSelector } from "../../store";
import { EmailAddressBox } from "../../components/form/EmailAddressBox";
import {
  deleteTransferRole,
  transferRoleSubmit,
} from "../../api/invitations";
import {
  LabelAndValue,
  SaveAndCancelButton,
} from "../../components/form";
import { Box, Stack } from "@mui/material";
import { invalidateMeetingsOverviewQueries } from "./utils";

type DelegateEmail = {
  email?: string;
};

type TransferRoleModalProps = {
  onClose: () => void;
  id?: string;
  delegateData?: DelegateEmail[] | null;
};

const normalizeEmail = (email: string) => email.trim().toLowerCase();

export default function TransferRoleModal({
  onClose,
  id,
  delegateData,
}: TransferRoleModalProps) {
  const initialEmails = useMemo(
    () =>
      (delegateData || [])
        .map((item) => item?.email)
        .filter((email): email is string => Boolean(email)),
    [delegateData]
  );

  const [emailIds, setEmailds] = useState<{
    list: string[];
    invalidEmailIds: string[];
  }>({
    list: initialEmails,
    invalidEmailIds: [],
  });
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const userData = useAppSelector((state) => state.auth.user);
  const [submitting, setSubmitting] = useState(false);

  const isUpdate = initialEmails.length > 0;

  const { addedEmails, deletedEmails } = useMemo(() => {
    const initialNormalized = new Set(initialEmails.map(normalizeEmail));
    const currentNormalized = new Set(emailIds.list.map(normalizeEmail));

    const addedEmails = emailIds.list.filter(
      (email) => !initialNormalized.has(normalizeEmail(email))
    );
    const deletedEmails = initialEmails.filter(
      (email) => !currentNormalized.has(normalizeEmail(email))
    );

    return { addedEmails, deletedEmails };
  }, [emailIds.list, initialEmails]);

  const hasChanges = addedEmails.length > 0 || deletedEmails.length > 0;

  const handleOnEmailAddressesChange = (payload) => {
    setEmailds(payload);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (emailIds.invalidEmailIds.length) return;

    setSubmitting(true);

    try {
      if (!isUpdate) {
        const response: any = await dispatch(
          transferRoleSubmit({
            emails: emailIds.list,
            ambassadorInvitationId: id,
          })
        );

        if (response.success) {
          invalidateMeetingsOverviewQueries(queryClient, userData?.role);
          onClose();
        }
        return;
      }

      let success = true;

      if (deletedEmails.length) {
        const deleteResponse: any = await dispatch(
          deleteTransferRole({
            ambassadorInvitationId: id,
            emails: deletedEmails,
          })
        );
        success = Boolean(deleteResponse?.success);
      }

      if (success && addedEmails.length) {
        const addResponse: any = await dispatch(
          transferRoleSubmit({
            emails: addedEmails,
            ambassadorInvitationId: id,
          })
        );
        success = Boolean(addResponse?.success);
      }

      if (success) {
        invalidateMeetingsOverviewQueries(queryClient, userData?.role);
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isSubmitDisabled = isUpdate
    ? !hasChanges || Boolean(emailIds.invalidEmailIds.length)
    : !emailIds.list.length || Boolean(emailIds.invalidEmailIds.length);

  return (
    <form onSubmit={onSubmit}>
      <Stack gap={2}>
        <Box>
          <LabelAndValue
            value={
              <EmailAddressBox
                inlineLabel={true}
                initialValue={initialEmails}
                onValueChange={handleOnEmailAddressesChange}
              />
            }
          />
        </Box>
        <SaveAndCancelButton
          primaryButtonText="Transfer"
          onCancelButtonClick={onClose}
          primaryButtonProps={{
            disabled: isSubmitDisabled,
            loading: submitting,
          }}
        />
      </Stack>
    </form>
  );
}
